import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { CompanyAssetDialog } from "./CompanyAssetDialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const CompanyAssets = () => {
  const { t } = useLanguage();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);

  const { data: assets, isLoading, refetch } = useQuery({
    queryKey: ['company-assets'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_assets')
        .select(`
          *,
          suppliers (
            id,
            name
          )
        `)
        .order('asset_name');
      
      if (error) throw error;
      return data;
    },
  });

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('company_assets')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success(t('deletedSuccessfully'));
      refetch();
    } catch (error) {
      console.error('Error deleting asset:', error);
      toast.error(t('errorDeletingData'));
    }
  };

  const getConditionBadge = (condition: string) => {
    const colors: any = {
      excellent: "bg-green-500",
      good: "bg-blue-500",
      fair: "bg-yellow-500",
      poor: "bg-red-500",
    };
    return <Badge className={colors[condition]}>{t(condition)}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('companyAssets')}</h3>
          <p className="text-sm text-muted-foreground">{t('assetManagement')}</p>
        </div>
        <Button onClick={() => { setSelectedAsset(null); setIsDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          {t('addAsset')}
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('assetName')}</TableHead>
              <TableHead>{t('assetCategory')}</TableHead>
              <TableHead>{t('supplier')}</TableHead>
              <TableHead>{t('currentValue')}</TableHead>
              <TableHead>{t('condition')}</TableHead>
              <TableHead className="text-right">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">{t('loading')}</TableCell>
              </TableRow>
            ) : assets?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">{t('noData')}</TableCell>
              </TableRow>
            ) : (
              assets?.map((asset) => (
                <TableRow key={asset.id}>
                  <TableCell className="font-medium">{asset.asset_name}</TableCell>
                  <TableCell>{t(asset.asset_category)}</TableCell>
                  <TableCell>{asset.suppliers?.name || '-'}</TableCell>
                  <TableCell>€{asset.current_value?.toFixed(2) || '0.00'}</TableCell>
                  <TableCell>{getConditionBadge(asset.condition)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedAsset(asset); setIsDialogOpen(true); }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(asset.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <CompanyAssetDialog
        open={isDialogOpen}
        onClose={() => { setIsDialogOpen(false); setSelectedAsset(null); refetch(); }}
        asset={selectedAsset}
      />
    </div>
  );
};